import { Box, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CopyContentButton } from '@percent/domain/giving'
import { Alert, Button, ButtonText, Icon } from '@percent/lemonade'
import classes from './CampaignDetailsSidebar.module.scss'
import { SidePanelContainer } from '@percent/workplace-giving/common/components/Layout/SidePanelContainer/SidePanelContainer'
import { useDateTimeFmt } from '@percent/workplace-giving/common/hooks/useDateTimeFmt/useDateTimeFmt'
import { useAnalytics } from '@percent/workplace-giving/common/hooks/useAnalytics/useAnalytics'
import { GivingCampaignStatusDto } from '@percent/workplace-giving/api/campaigns/campaign.types'
import type { GivingCampaignDto } from '@percent/workplace-giving/api/campaigns/campaign.types'
import { dayjs } from '@percent/workplace-giving/utils/dayjs/dayjs'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { usePermission } from '@percent/workplace-giving/common/hooks/usePermission/usePermission'
import { permission } from '@percent/workplace-giving/common/hoc/withPermissions'
import { Permissions } from '@percent/workplace-giving/constants/permissions'

type CampaignDetailsSidebarProps = {
  campaign: GivingCampaignDto
}

export function CampaignDetailsSidebar({ campaign }: Readonly<CampaignDetailsSidebarProps>) {
  const { t } = useTranslation()
  const { track } = useAnalytics()
  const { formatDateRange } = useDateTimeFmt()
  const navigate = useNavigate()
  const has = usePermission()

  const { id, startDate, endDate } = campaign
  const campaignEnded = endDate ? dayjs(endDate) <= dayjs(new Date()) : false
  const campaignCancelled = campaign.status === GivingCampaignStatusDto.CANCELLED

  const scrollToActionsSections = () => {
    const sessionsSection = document.getElementById('actions-section')

    sessionsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const canEdit = has(permission(Permissions.CampaignsWrite))

  return (
    <SidePanelContainer>
      <Box data-testid="campaign-details-sidebar" className={classes.Wrapper}>
        {campaignCancelled ? (
          <Alert variant="warning" title={t('workplace_giving.campaigns.alert.campaignCancelled')} />
        ) : null}
        {campaignEnded ? <Alert variant="info" title={t('workplace_giving.campaigns.alert.campaignEnded')} /> : null}

        {startDate && endDate ? (
          <Box className={classes.DetailsContainer} data-testid="campaign-details-sidebar-details-container">
            <Box className={campaignCancelled ? classes.Cancelled : undefined}>
              <Icon name="calendar" size="m" color="neutral300" />
              <Box>
                <Text>{formatDateRange(new Date(startDate), new Date(endDate))}</Text>
              </Box>
            </Box>
          </Box>
        ) : null}

        <Box className={classes.DetailsContainer}>
          <Button stretch size="large" shape="oval" onClick={scrollToActionsSections}>
            {t('workplace_giving.campaigns.howCanIHelp')}
          </Button>

          <CopyContentButton
            stretch
            size="large"
            shape="oval"
            content={window.location.href}
            onClick={() => {
              track({
                event: 'Giving Campaign Link Sharing',
                properties: {
                  campaignId: id
                }
              })
            }}
          >
            {t('workplace_giving.common.copyShareLink')}
          </CopyContentButton>
        </Box>
      </Box>
      <Box
        style={{
          display: 'flex',
          marginRight: '1rem',
          justifyContent: 'flex-end',
          marginTop: '1.5rem'
        }}
      >
        {canEdit && !campaignCancelled && (
          <ButtonText
            onClick={() => {
              track({
                event: 'Giving Campaign Edit Start',
                properties: {
                  origin: 'Campaign Details'
                }
              })
              navigate(AppRoute.EDIT_CAMPAIGN.replace(':campaignId', id))
            }}
            data-testid="campaign-edit-button"
          >
            {t('workplace_giving.common.edit')}
          </ButtonText>
        )}
      </Box>
    </SidePanelContainer>
  )
}
